import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

const COLLAPSED_KEY = "echoes_sidebar_collapsed"

type ShellState = {
  /** Desktop: the sidebar is hidden to give the list the full width. */
  sidebarCollapsed: boolean
  /** Small screens: the sidebar sheet is open. */
  sidebarSheetOpen: boolean
  commandMenuOpen: boolean
  shortcutHelpOpen: boolean
  createProjectOpen: boolean
  toggleSidebar: () => void
  setSidebarSheetOpen: (open: boolean) => void
  setCommandMenuOpen: (open: boolean) => void
  setShortcutHelpOpen: (open: boolean) => void
  setCreateProjectOpen: (open: boolean) => void
}

const ShellStateContext = createContext<ShellState | null>(null)

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1"
  } catch {
    return false
  }
}

export function ShellStateProvider({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsed)
  const [sidebarSheetOpen, setSidebarSheetOpen] = useState(false)
  const [commandMenuOpen, setCommandMenuOpen] = useState(false)
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false)
  const [createProjectOpen, setCreateProjectOpen] = useState(false)

  const toggleSidebar = useCallback(() => {
    // One command for both layouts: below `md` there is no docked sidebar to
    // collapse, so it opens the sheet instead.
    if (window.matchMedia("(min-width: 768px)").matches) {
      setSidebarCollapsed((collapsed) => {
        const next = !collapsed
        try {
          localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0")
        } catch {
          // Private mode: the choice just does not survive a reload.
        }
        return next
      })
    } else {
      setSidebarSheetOpen((open) => !open)
    }
  }, [])

  const value = useMemo(
    () => ({
      sidebarCollapsed,
      sidebarSheetOpen,
      commandMenuOpen,
      shortcutHelpOpen,
      createProjectOpen,
      toggleSidebar,
      setSidebarSheetOpen,
      setCommandMenuOpen,
      setShortcutHelpOpen,
      setCreateProjectOpen,
    }),
    [
      sidebarCollapsed,
      sidebarSheetOpen,
      commandMenuOpen,
      shortcutHelpOpen,
      createProjectOpen,
      toggleSidebar,
    ],
  )

  return <ShellStateContext.Provider value={value}>{children}</ShellStateContext.Provider>
}

export function useShellState(): ShellState {
  const state = useContext(ShellStateContext)
  if (!state) throw new Error("useShellState must be used inside <ShellStateProvider>")
  return state
}
